import { useContext, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, Sun, Moon, LogOut, Store,
  ChevronDown, PanelLeftOpen,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useTheme } from './ThemeProvider';
import ContextStore from '../../hooks/ContextStore';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';

/* ── Page title map ─────────────────────────────────────────────────── */
const PAGE_TITLES: Record<string, string> = {
  '/smart-analytics':    'Smart Analytics',
  '/dashboard':          'Dashboard',
  '/productgroup/order': 'Products',
  '/productgroup/update':'Products',
};

function getPageTitle(pathname: string): string {
  for (const [key, val] of Object.entries(PAGE_TITLES)) {
    if (pathname.startsWith(key)) return val;
  }
  return 'JumiaDash';
}

interface TopbarProps {
  onMenuClick: () => void;
  onSidebarToggle: () => void;
  sidebarCollapsed: boolean;
  stores: any[];
  onStoreChange: (storeId: string) => void;
}

export function Topbar({ onMenuClick, onSidebarToggle, sidebarCollapsed, stores, onStoreChange }: TopbarProps) {
  const { contextStore } = useContext(ContextStore);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [storeOpen, setStoreOpen] = useState(false);

  const currentStore = contextStore?.store;
  const user = contextStore?.user;
  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : 'JD';

  const pageTitle = getPageTitle(location.pathname);

  const handleLogout = useCallback(async () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }, []);

  const handleStoreSelect = (storeId: string) => {
    onStoreChange(storeId);
    setStoreOpen(false);
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/95 backdrop-blur-sm px-4 lg:px-6">

      {/* ── Hamburger (mobile) ────────────────────────── */}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onMenuClick}
        className="lg:hidden text-muted-foreground"
        aria-label="Open menu"
      >
        <Menu className="h-4 w-4" />
      </Button>

      {/* ── Sidebar expand button (desktop, shown only when sidebar is collapsed) ── */}
      {sidebarCollapsed && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onSidebarToggle}
          className="hidden lg:flex text-muted-foreground"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </Button>
      )}

      {/* ── Page title ───────────────────────────────── */}
      <div className="flex-1 min-w-0">
        <h1 className="text-sm font-semibold text-foreground truncate">
          {pageTitle}
        </h1>
      </div>

      {/* ── Right actions ────────────────────────────── */}
      <div className="flex items-center gap-1.5">

        {/* Store selector */}
        {stores.length > 0 && (
          <DropdownMenu open={storeOpen} onOpenChange={setStoreOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 max-w-[180px]">
                <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate text-xs">
                  {currentStore?.name || 'All Stores'}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Select Store</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleStoreSelect('0')}
                className={cn(!currentStore?._id && 'bg-accent')}
              >
                <span className="flex-1">All Stores</span>
                {!currentStore?._id && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1">Active</Badge>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {stores.map((store) => (
                <DropdownMenuItem
                  key={store._id}
                  onClick={() => handleStoreSelect(store._id)}
                  className={cn(currentStore?._id === store._id && 'bg-accent')}
                >
                  <span className="flex-1 truncate">{store.name}</span>
                  <div className={cn(
                    'h-1.5 w-1.5 rounded-full shrink-0',
                    store.status ? 'bg-emerald-500' : 'bg-red-400'
                  )} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* new-order sound bell (components/common/OrderAlerts.tsx renders into this slot) */}
        <span id="order-bell-slot" className="inline-flex" />

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="text-muted-foreground hover:text-foreground"
        >
          {theme === 'dark'
            ? <Sun className="h-4 w-4" />
            : <Moon className="h-4 w-4" />
          }
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="rounded-full">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="font-normal">
              <p className="text-xs font-medium text-foreground truncate">{user?.email}</p>
              <p className="text-[10px] text-muted-foreground">Administrator</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
